/**
 * Field pickers, grouped the way the form is actually built.
 *
 * Two fields can share a label — a "Name" in Billing and a "Name" in Shipping
 * is not a mistake, it is how forms read. A flat list of labels makes them
 * indistinguishable, and the only thing separating them is their position in a
 * dropdown, which is not something anybody should have to count.
 *
 * The Logic page solved this by grouping options under Section, then
 * Section › Card, and printing each option as `Label (key)`. Every other picker
 * in the admin — the workflow's mapping rows above all, where getting the wrong
 * "Name" means sending the wrong value to a Monday column — wants exactly the
 * same treatment, so the arrangement lives here rather than inside one page.
 *
 * Where a card SITS is not in the layout: cards nest, and which container a
 * card belongs to and which card it nests in are answered by the field spine
 * (the card markers the builder keeps alongside the real fields). That is why
 * `sectionsFromSpine` reads both.
 */
import type { FieldDefinition, FormLayout, FormWidget } from '../../shared/types';
import { flattenWidgetTree } from '../../shared/types';
import { CompositeSub, compositeSubs } from '../../shared/address';

/** A section, and the cards in it, as the pickers need them. */
export interface SecInfo {
  id: string;
  title: string;
  cards: { id: string; title: string; scope: string; parent: string }[];
}

/** One `<optgroup>`: a heading, and the options under it. */
export interface OptGroup { label: string; opts: { v: string; l: string }[] }

/** Just enough of a draft field to place a card. */
interface SpineField { section?: string; cardId?: string; scope?: string; group?: string }
interface SpineSection { id: string; title?: string; cards?: { id: string; title?: string; scope?: string }[] }

/**
 * Sections and their cards, read off the builder's working copy.
 *
 * The layout says a card exists and what it is called; the spine says where it
 * lives. Both are needed, because "Contact" nested inside "Billing" and
 * "Contact" nested inside "Shipping" are only tellable apart by the nesting.
 */
export function sectionsFromSpine(
  sections: SpineSection[] | undefined,
  fields: SpineField[] | undefined
): SecInfo[] {
  const place = new Map((fields || [])
    .filter((f) => f.cardId)
    .map((f) => [`${f.section}:${f.cardId}`, { scope: f.scope, parent: f.group }]));
  return (sections || []).map((s) => ({
    id: s.id,
    title: s.title || s.id,
    cards: (s.cards || []).map((c) => {
      const at = place.get(`${s.id}:${c.id}`);
      return { id: c.id, title: c.title || '', scope: at?.scope || c.scope || 'order', parent: at?.parent || '' };
    }),
  }));
}

/**
 * Sections and their cards off a PUBLISHED layout, for a picker that has no
 * draft in hand (another form's questions, or a tab reading the published
 * definition). The layout does not say how cards nest, so an inner card reads
 * `Section › Card` rather than `Section › Outer › Card` — still its own group.
 */
export function sectionsFromLayout(layout: Pick<FormLayout, 'sections'> | null | undefined): SecInfo[] {
  return (layout?.sections || []).map((s) => ({
    id: s.id,
    title: s.title || s.id,
    cards: (s.cards || []).map((c) => ({ id: c.id, title: c.title || '', scope: c.scope || 'order', parent: '' })),
  }));
}

/** The cards of a section belonging to one container ('order' or a widget scope). */
export const cardsOf = (s: SecInfo, scope: string) => s.cards.filter((c) => c.scope === scope);

/**
 * A card's name as the form reads it.
 *
 * Cards nest, so an inner card is only tellable apart by the cards it sits in:
 * "Contact" alone could be either of two, "Billing › Contact" could not.
 */
export function cardPath(s: SecInfo, card: SecInfo['cards'][number]): string {
  const names: string[] = [];
  const seen = new Set<string>();
  let cur: SecInfo['cards'][number] | undefined = card;
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    names.unshift(cur.title || '(untitled card)');
    cur = cur.parent ? s.cards.find((x) => x.id === cur!.parent && x.scope === card.scope) : undefined;
  }
  return names.join(' › ');
}

/** How one field becomes one or more options — a composite adds its sub-paths. */
export type Expand = (f: FieldDefinition) => { v: string; l: string }[];

/**
 * A question the form has not published yet, flagged so every picker says so.
 *
 * It is on `ui_meta` rather than baked into the label because the key has to
 * sit with the NAME — "A new question (newThing) — not published yet" reads,
 * "A new question — not published yet (newThing)" does not.
 */
export const NOT_PUBLISHED_FLAG = '__notPublished';
export const NOT_PUBLISHED_NOTE = '— not published yet';
const note = (f: FieldDefinition) =>
  ((f.ui_meta as Record<string, unknown> | undefined)?.[NOT_PUBLISHED_FLAG] ? ` ${NOT_PUBLISHED_NOTE}` : '');

/** The default: the field itself, named so two same-labelled ones differ. */
export const plainOption: Expand = (f) => [{ v: f.key, l: `${f.label} (${f.key})${note(f)}` }];

/**
 * The sub-paths a composite question offers ([] for an ordinary one).
 *
 * A Name answer and an Address answer are each stored as one flat object, and
 * every reader in the product addresses a part of one the same way — one dot
 * deep, `shipAddress.city`. That is true of the Logic page's conditions
 * (`valueAt` in server/src/lib/conditions.ts), of every delivery template
 * (`lookup` in services/delivery/template.ts) and of a Monday column source.
 * So the list of parts belongs here, next to the pickers, rather than in the
 * one page that happened to need it first.
 */
export const subsOf = (f: FieldDefinition): CompositeSub[] =>
  (f.input_type === 'name' ? compositeSubs('name')
    : f.input_type === 'address' ? compositeSubs('address', f.ui_meta?.address?.kind === 'shipping')
    : []);

/** One sub-path as an option: `Ship To › City`, keyed `shipTo.city`. */
export const subOption = (f: FieldDefinition, s: CompositeSub) =>
  ({ v: `${f.key}.${s.sub}`, l: `${f.label} › ${s.label}` });

/**
 * The field, then each part of it — what every picker that READS a value wants.
 *
 * The combined `fullAddress`/`fullName` are kept: they are readable, and the
 * whole-field option above them means something different in a template (an
 * address renders as one line) than it does in a condition. Only a picker
 * choosing somewhere to WRITE has to drop them, which is why `derived` is on
 * the sub rather than decided here.
 */
export const withSubs: Expand = (f) => [...plainOption(f), ...subsOf(f).map((s) => subOption(f, s))];

/**
 * Every group for ONE container, in the order the form draws them.
 *
 * A section's loose fields head it, then one group per card. `strayLabel`
 * catches fields whose section no longer exists — their answers are real and
 * a picker that silently dropped them would be lying about what the form asks.
 */
export function groupsForContainer(
  scope: string,
  fields: FieldDefinition[],
  sections: SecInfo[],
  expand: Expand = plainOption,
  opts: { strayLabel?: string; prefix?: string } = {}
): OptGroup[] {
  const mine = fields.filter((f) => f.scope === scope);
  if (!mine.length) return [];
  const name = (label: string) => (opts.prefix ? `${opts.prefix} › ${label}` : label);
  const groups: OptGroup[] = [];

  for (const s of sections) {
    const inSec = mine.filter((f) => f.section === s.id);
    if (!inSec.length) continue;
    const cards = cardsOf(s, scope);
    const cardIds = new Set(cards.map((c) => c.id));
    const loose = inSec.filter((f) => !f.ui_meta?.group || !cardIds.has(f.ui_meta.group));
    if (loose.length) groups.push({ label: name(s.title), opts: loose.flatMap(expand) });
    for (const c of cards) {
      const inCard = inSec.filter((f) => f.ui_meta?.group === c.id);
      if (inCard.length) groups.push({ label: name(`${s.title} › ${cardPath(s, c)}`), opts: inCard.flatMap(expand) });
    }
  }

  const known = new Set(sections.map((s) => s.id));
  const stray = mine.filter((f) => !known.has(f.section));
  if (stray.length) groups.push({ label: name(opts.strayLabel || 'Other fields'), opts: stray.flatMap(expand) });
  return groups;
}

/** Each repeating container's name ("Location", "Device"), by scope, off the widget trees. */
export function scopeLabelsOf(widgets: FormWidget[] | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const w of widgets || []) {
    if (!w.tree) continue;
    for (const n of flattenWidgetTree(w.tree)) out[n.scope] = n.itemLabel || n.scope;
  }
  return out;
}

/**
 * Every group a picker over the whole form draws: the main form first, then
 * each repeating container prefixed by its name ("Every Location › Contact").
 * With no sections to arrange by, everything sits under one "Questions" group
 * — still `Label (key)`, so nothing is ever a bare label.
 */
export function pickerGroups(
  fields: FieldDefinition[],
  sections: SecInfo[],
  widgets: FormWidget[] = [],
  expand: Expand = plainOption,
  opts: { repeat?: boolean } = {}
): OptGroup[] {
  const order = groupsForContainer('order', fields, sections, expand, sections.length ? {} : { strayLabel: 'Questions' });
  if (opts.repeat === false) return order;
  const labels = scopeLabelsOf(widgets);
  const repeats = repeatScopesIn(fields, widgets).flatMap((scope) => groupsForContainer(
    scope, fields, sections, expand, { prefix: `Every ${labels[scope] || scope}`, strayLabel: 'Other fields' }
  ));
  return [...order, ...repeats];
}

/** Does any group offer `v`? */
export const groupsHave = (groups: OptGroup[], v: string): boolean => groups.some((g) => g.opts.some((o) => o.v === v));

/** The same groups with only the options `keep` allows, empty groups dropped. */
export function filterGroups(groups: OptGroup[], keep: (v: string) => boolean): OptGroup[] {
  return groups.map((g) => ({ label: g.label, opts: g.opts.filter((o) => keep(o.v)) })).filter((g) => g.opts.length);
}

/**
 * Repeating containers worth showing: every widget node, plus any scope that
 * still has fields but whose container is gone — those answers are real too.
 */
export function repeatScopesIn(fields: FieldDefinition[], widgets: FormWidget[]): string[] {
  return [...new Set([
    ...widgets.flatMap((w) => (w.tree ? flattenWidgetTree(w.tree) : [])).map((n) => n.scope),
    ...fields.filter((f) => f.scope !== 'order').map((f) => f.scope),
  ])];
}
